import {
  isoWeekKey,
  LEADERBOARD_TZ,
  type AgentLeaderboardEntry,
  type AgentTier,
} from '@kilat/shared';

/**
 * Pure, React-free view logic for the weekly agent leaderboard tile.
 *
 * Kept env-agnostic (no Firestore SDK, no React, no i18n runtime) so the
 * own-rank lookup and the current-week key are unit-testable in isolation —
 * mirrors `operators-view.ts` / `ledger-activity.ts`. Only i18n KEYS cross this
 * boundary (`TIER_LABEL_KEYS`); the tile resolves them via `useI18n()` so copy
 * stays translatable.
 */

/** i18n key for each agent tier's badge label. */
export const TIER_LABEL_KEYS: Record<AgentTier, string> = {
  bronze: 'agent.leaderboard.tierBronze',
  silver: 'agent.leaderboard.tierSilver',
  gold: 'agent.leaderboard.tierGold',
};

export interface OwnRank {
  /** 1-based position of the agent within the shown, pre-sorted entries. */
  readonly rank: number;
  readonly entry: AgentLeaderboardEntry;
}

/**
 * Find the signed-in agent's row in the week's entries.
 *
 * The backend keeps `entries` pre-sorted DESC by `signupCount` (ties broken by
 * `agentId`) and capped at `LEADERBOARD_TOP_N`, so rank is simply the 1-based
 * array position — no client-side sort. Returns `null` when the agent has no id
 * or has no row in the retained top-N (the tile then shows the graceful
 * "not ranked yet this week" copy instead of a dead-end).
 */
export function findOwnRank(
  entries: readonly AgentLeaderboardEntry[],
  agentId: string | null,
): OwnRank | null {
  if (!agentId) return null;
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (entry && entry.agentId === agentId) return { rank: i + 1, entry };
  }
  return null;
}

/**
 * Current ISO-8601 week key (`YYYY-Www`) in the brand market timezone — the
 * `weeks/{weekKey}` doc id the tile reads. Single-sourced from the shared
 * `isoWeekKey` + `LEADERBOARD_TZ` so the read path can never disagree with the
 * `leaderboardOnSignup` trigger about which week is "this week".
 */
export function currentWeekKey(nowMs: number = Date.now()): string {
  return isoWeekKey(nowMs, LEADERBOARD_TZ);
}
